import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import * as dayjs from 'dayjs'
import * as dayjs_plugin_utc from 'dayjs/plugin/utc'
// import Plot from 'react-plotly.js';

import { getEventRecordData, processEventRecordData } from '../../api/kquake'
import { getLoginStatus } from '../../api/sso'
import { LOGIN_AND_CLOSE_URL } from '../../config'
import { setLoginStatus } from '../../store/userSlice'

dayjs.extend(dayjs_plugin_utc)


const Plot = React.lazy(() => import('react-plotly.js'));


const DATA_TYPES = ['ACC', 'VEL', 'DISP', 'ACC_SPECTRA'];
const DATA_COMPONENTS = ['E', 'N', 'Z']
const PLOT_COMPONENTS_AND_YAXISES = [['E', 'y'], ['N', 'y2'], ['Z', 'y3']]

const PRUPOSES_OF_USE = [
  'study_natural_science',
  'study_engineering',
  'seismic_desing',
  'educational',
  'etc',
]

const PROCESS_DEFAULT_PARAMETERS = {
  timewindow: {
    beginning: 0,
    end: 0,
  },
  cosinetaper: {
    beginning: 1,
    end: 1,
  },
  bandpassfilter: {
    lowcut: 0.1,
    highcut: 25.0,
    order: 2,
  },
  postcosinetaper: {
    beginning: 1,
    end: 1,
  },
  baselinecorrection: {
    domain: 'VEL',
    order: 4,
  }
}

const PROCESS_PARAMETERS_RANGE = {
  cosinetaper: {
    min: 0,
    max: 50,
  },
  bandpassfilter : {
    min: 0,
    max: 50,
  }
}
const _refine_form_data = (data, timeWindow) => {
  const processParameters = {};

  const { timewindow, cosinetaper, bandpassfilter, postcosinetaper, baselinecorrection } = data;

  // 1. TimeWindow
  if (timewindow.beginning && timewindow.beginning.value) {
    const value = parseInt(timewindow.beginning.value)
    if (value < 0 || value >= timeWindow) {
      throw new Error(`Time window의 At the beginning 값이 유효하지 않습니다.`);
    }
    processParameters.margin_beginning = value
  }
  if (timewindow.end && timewindow.end.value) {
    const value = parseInt(timewindow.end.value)
    if (value <= 0 || value > timeWindow || (processParameters.margin_beginning && processParameters.margin_beginning >= value)) {
      throw new Error(`Time window의 At the end 값이 유효하지 않습니다.`);
    }
    processParameters.margin_end = timeWindow - value
  }

  // 2. Cosine Taper
  if (cosinetaper) {
    const minValue = PROCESS_PARAMETERS_RANGE.cosinetaper.min;
    const maxValue = PROCESS_PARAMETERS_RANGE.cosinetaper.max;
    if (cosinetaper.beginning && cosinetaper.beginning.enabled) {
      const value = parseInt(cosinetaper.beginning.value);
      if (Number.isNaN(value) ||
          (value < minValue) ||
          (value > maxValue)) {
        throw new Error(`Cosine Taper의 At the beginning 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.pre_cosine_taper_beginning = value;
    }
  
    if (cosinetaper.end && cosinetaper.end.enabled) {
      const value = parseInt(cosinetaper.end.value)
      if (Number.isNaN(value) ||
          (value < minValue) ||
          (value > maxValue)) {
        throw new Error(`Cosine Taper의 At the end 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.pre_cosine_taper_end = value;
    }
  }

  // 3. Bandpass Filter
  if (bandpassfilter) {
    const minValue = PROCESS_PARAMETERS_RANGE.bandpassfilter.min;
    const maxValue = PROCESS_PARAMETERS_RANGE.bandpassfilter.max;
    if (bandpassfilter.lowcut && bandpassfilter.lowcut.enabled) {
      const value = parseFloat(bandpassfilter.lowcut.value)
      if (Number.isNaN(value) ||
          (value <= minValue) ||
          (value >= maxValue)) {
        throw new Error(`Bandpass Filter의 Low-cut 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.bandpass_low_cut = value;
    }
  
    if (bandpassfilter.highcut && bandpassfilter.highcut.enabled) {
      const value = parseFloat(bandpassfilter.highcut.value)
      if (Number.isNaN(value) ||
          (value <= minValue) ||
          (value >= maxValue)) {
        throw new Error(`Bandpass Filter의 High-cut 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.bandpass_high_cut = value;
    }
    if (bandpassfilter.lowcut.enabled || bandpassfilter.highcut.enabled) {
      processParameters.bandpass_order = parseInt(bandpassfilter.order.value);
    }
  }

  // 5. Post-Cosine Taper
  if (postcosinetaper) {
    const minValue = PROCESS_PARAMETERS_RANGE.cosinetaper.min;
    const maxValue = PROCESS_PARAMETERS_RANGE.cosinetaper.max;
    if (postcosinetaper.beginning && postcosinetaper.beginning.enabled) {
      const value = parseInt(postcosinetaper.beginning.value)
      if (Number.isNaN(value) ||
          (value < minValue) ||
          (value > maxValue)) {
        throw new Error(`Post-Cosine Taper의 At the beginning 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.post_cosine_taper_beginning = value;
    }
    if (postcosinetaper.end && postcosinetaper.end.enabled) {
      const value = parseInt(postcosinetaper.end.value)
      if (Number.isNaN(value) ||
          (value < minValue) ||
          (value > maxValue)) {
        throw new Error(`Post-Cosine Taper의 At the end 값이 유효하지 않습니다. (${minValue} ~ ${maxValue})`);
      }
      processParameters.post_cosine_taper_end = value;
    }
  }

  // 6. Baseline Correction
  if (baselinecorrection && baselinecorrection.enabled) {
    processParameters.baseline_correction_fitting_domain = baselinecorrection.domain;
    processParameters.baseline_correction_order = parseInt(baselinecorrection.order);
  }
 
  return processParameters;
}


const _saveAsTextFile = (dataType, record, data, processParameters, processedTime) => {
  console.log(dataType, record, data, processParameters, processedTime)
  const eventOriginTimeStr = dayjs.unix(record.eventOriginTime).utc().format('YYYY-MM-DDTHH:mm:ssZ')
  const typeAndUnit = _getDataTypeAndUnit(dataType)
  const isBandpassFilterApplied = ((processParameters.bandpass_low_cut || processParameters.bandpass_high_cut) ? true : false)
  const isBaselineCorrectionApplied = (processParameters.baseline_correction_fitting_domain ? true : false)

  const refDataType = (dataType === 'acc_spectra') ? 'acc' : dataType
  DATA_COMPONENTS.forEach(component => {
    const firstSampleTimestamp = dayjs.unix(data[refDataType][component].startTime).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    const lastSampleTimestamp = dayjs.unix(data[refDataType][component].endTime).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    const sampleDelta = data[refDataType][component].delta
    const recordDuration = data[refDataType][component].endTime - data[refDataType][component].startTime + sampleDelta
    const header = `HEADER_FORMAT_VERSION: 1.0
  
## EARTHQUAKE EVENT INFO (KMA)
EVENT_DATE_TIME: ${eventOriginTimeStr}
EVENT_LATITUDE_DEGREE: ${record.evnetLatitude}
EVENT_LONGITUDE_DEGREE: ${record.evnetLongitude}
EVENT_DEPTH_KM: ${(record.evnetDepth || record.evnetDepth === 0) ? record.evnetDepth.toFixed(1) : 'N/A'}
EVENT_MAGNITUDE_L: ${record.evnetMagnitudeML.toFixed(1)}

## STATION & SENSOR INFO
NETWORK_CODE: ${record.network}
STATION_CODE: ${record.stationCode}
STATION_LATITUDE_DEGREE: ${record.stationLatitude}
STATION_LONGITUDE_DEGREE: ${record.stationLongitude}
STATION_ELEVATION_M: ${record.stationElevation.toFixed(1)}
PREFERRED_SITE_VS30_M/S:
PREFERRED_SITE_CLASSIFICATION_NEHRP:
SENSOR_DEPTH_M: ${record.sensorDepth.toFixed(1)}
SENSOR_MODEL_NAME: ${record.sensorModel}

## RECORD INFO
STREAM_ID: ${data[refDataType][component].streamId}
EPICENTRAL_DISTANCE_KM: ${record.epicentralDistance.toFixed(2)}
EARTHQUAKE_BACKAZIMUTH_DEGREE: ${record.backAzimuth.toFixed(2)}
MEASURED_DATA_TYPE: ACCELERATION
SAMPLING_INTERVAL_S: ${sampleDelta.toFixed(2)}
NUMBER_OF_SAMPLES: ${data[refDataType][component].values.length}
DATE_TIME_FIRST_SAMPLE: ${firstSampleTimestamp}
DATE_TIME_LAST_SAMPLE: ${lastSampleTimestamp}
DURATION_S: ${recordDuration.toFixed(2)}

## PROCESSING INFO
REMOVE_DC_OFFSET: LINEAR
PRE_COSINE_TAPER_AT_THE_BEGINNING_%: ${processParameters.pre_cosine_taper_beginning}
PRE_COSINE_TAPER_AT_THE_END_%: ${processParameters.pre_cosine_taper_end}
ZERO_PADDING: ${isBandpassFilterApplied ? 'UP TO THE NEXT HIGHER POWER OF 2' : 'NOT APPLIED'}
FILTER_TYPE: ${isBandpassFilterApplied ? 'BUTTERWORTH' : 'NOT APPLIED'}
FILTER_ORDER: ${isBandpassFilterApplied ? processParameters.bandpass_order : ''}
LOW_CUT_FREQUENCY_HZ: ${isBandpassFilterApplied ? processParameters.bandpass_low_cut: ''}
HIGH_CUT_FREQUENCY_HZ: ${isBandpassFilterApplied ? processParameters.bandpass_high_cut: ''}
REMOVE_ZERO_PADDING: ${isBandpassFilterApplied ? 'APPLIED' : ''}
POST_COSINE_TAPER_AT_THE_BEGINNING_%: ${processParameters.post_cosine_taper_beginning}
POST_COSINE_TAPER_AT_THE_END_%: ${processParameters.post_cosine_taper_end}
BASELINE_CORRECTION_TYPE: ${isBaselineCorrectionApplied ? 'POLYNOMIAL' : 'NOT APPLIED'}
BASELINE_CORRECTION_FITTING_DOMAIN: ${isBaselineCorrectionApplied ? processParameters.baseline_correction_fitting_domain.toUpperCase() : ''}
BASELINE_CORRECTION_POLINOMIAL_ORDER: ${isBaselineCorrectionApplied ? processParameters.baseline_correction_order : ''}
OUTPUT_DATA_TYPE: ${typeAndUnit.dataType}
OUTPUT_UNITS: ${typeAndUnit.dataUnit}

## OUTPUT
`
    let content;
    if (dataType === 'acc_spectra') {
      content = header + data[dataType][component].slice(1).map((v, idx)=> `${((idx + 1) * data[dataType].period[2]).toFixed(2)}, ${v.toFixed(6)}`).join('\n')
    } else {
      content = header + data[dataType][component].values.map(v => v.toFixed(6)).join('\n')
    }
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const fileName = `${record.id}_${dataType}.${component}_${processedTime}.txt`
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.setAttribute('download', fileName)
    a.click()    
  }) 
}


const _getDataTypeAndUnit = (dataType) => {
  if (dataType === 'acc') { 
    return { dataType: 'ACCELERATION', dataUnit: 'cm/s^2' }
  } else if (dataType === 'vel') { 
    return { dataType: 'VELOCITY', dataUnit: 'cm/s' }
  } else if (dataType === 'disp') { 
    return { dataType: 'DISPLACEMENT', dataUnit: 'cm' }
  } else if (dataType === 'acc_spectra') { 
    return { dataType: 'ACCELERATION RESPONSE SPECTRUM', dataUnit: 'cm/s^2' }
  }
}


export const EventRecordProcessModal = ({ record, onClose, dataCache, setDataCache, setIsLoading, downloadPurpose, setDownloadPurpose }) => {
  useEffect(() => {
    // Init select element
    // https://materializecss.com/select.html
    const elems = document.querySelectorAll('select');
    window.M.FormSelect.init(elems);
  }, [])

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isLoggedIn = useSelector(state => state.user.login)
  const apiToken = useSelector(state => state.user.apiToken)
  const { register, handleSubmit } = useForm();
  const [processParameters, setProcessParameters] = useState({})
  const [processedData, setProcessedData] = useState({})
  const [processedTime, setProcessedTime] = useState({})
    // const onSubmit = data => console.log(data);

  // const [downloadPurpose, _setDownloadPurpose] = useState('')
  const [agreeToTermsOfUse, setAgreeToTermsOfUse] = useState(false)

  // const setDownloadPurpose = (purpose) => {
  //   window.sessionStorage.setItem('downloadPurpose', purpose)
  //   _setDownloadPurpose(purpose)
  // }

  // useEffect(() => {
  //   const purpose = window.sessionStorage.getItem('downloadPurpose')
  //   if (purpose in PRUPOSES_OF_USE) {
  //     setDownloadPurpose(purpose)
  //   }
  // }, [])

  const onSubmit = async (data) => {
    if (!downloadPurpose) {
      alert(t('form.message.selectPurpose'))
      return
    }
    
    let logInStatus = {}
    if (!isLoggedIn) {
      setIsLoading(true)
      logInStatus = await getLoginStatus()
      if (logInStatus?.status === 200) {
        dispatch(setLoginStatus({...logInStatus}))
      }
      setIsLoading(false)
    }

    if (!isLoggedIn && logInStatus?.status !== 200) {
      if (window.confirm(t('form.message.clickDownloadAfterLogin'))) {
        const loginWindow = window.open(LOGIN_AND_CLOSE_URL, '_blank')
        if (loginWindow) {
          loginWindow.focus()
        }
      }
      return
    }
    const _apiToken = logInStatus?.status === 200 ? logInStatus.token : apiToken
    // const _apiToken = '__test__'

    let _processParameters = {}
    let _processedData = {}
    try {
      _processParameters = _refine_form_data(data, parseInt(record.endTime - record.startTime));
    } catch (e) {
      alert(e.message);
      return;
    }
    // console.log(_processParameters);

    setIsLoading(true)
    try {
      _processedData = await processEventRecordData(record.id, _processParameters, downloadPurpose, _apiToken)
    } catch (error) {
      const alertMessage = [
        t('form.message.failedToProcess'),
        `Error message:${error.message}`,
        t('form.message.contactAdministrator'),
      ]
      alert(alertMessage.join('\r\n'))
      setIsLoading(false)
      return
    }
    setProcessedData(_processedData)
    setProcessParameters(_processParameters)
    setProcessedTime(Date.now())
    setIsLoading(false)
  }

  useEffect(() => {
    const dataTypesToFetch = []
    if (record.id in dataCache) {
      if (!('preprocessed' in dataCache[record.id])) {
        dataTypesToFetch.push('preprocessed')
      }
    } else {
      dataTypesToFetch.push('preprocessed')
    }

    if (dataTypesToFetch.length > 0) {
      const fetchData = async (recordId) => {
        const data = await getEventRecordData(recordId, dataTypesToFetch);
        setDataCache({...dataCache, [record.id]: {...dataCache[record.id], ...data}})
      }
      fetchData(record.id)
    }
  }, [record, dataCache, setDataCache])

  const [displayDataType, _setDisplayDataType] = useState(DATA_TYPES[0]);
  const setDisplayDataType = (dataType) => {
    if (dataType.toLowerCase() in processedData) {
      _setDisplayDataType(dataType)
    } else {
      alert(t('page.kesmdb.notingToDisplay') + '\r\n' + t('page.kesmdb.clickRunButtonFirst'))
    }
  }

  const [plotData, setPlotData] = useState([]);
  const [plotLayout, setPlotLayout] = useState({});

  useEffect(() => {
    const _plotData = []
    if (displayDataType === 'ACC_SPECTRA') { 
      if ('acc_spectra' in processedData) {
        _plotData.push({
          type: 'scatter',
          mode: 'lines',
          name: 'EW',
          x0: processedData.acc_spectra.period[0],
          dx: processedData.acc_spectra.period[2],
          y: processedData.acc_spectra.E,
          line: {
            color: 'blue',
            width: 2,
          },
        })
        _plotData.push({
          type: 'scatter',
          mode: 'lines',
          name: 'NS',
          x0: processedData.acc_spectra.period[0],
          dx: processedData.acc_spectra.period[2],
          y: processedData.acc_spectra.N,
          line: {
            color: 'black',
            width: 2,
          },
        })
        _plotData.push({
          type: 'scatter',
          mode: 'lines',
          name: 'UD',
          x0: processedData.acc_spectra.period[0],
          dx: processedData.acc_spectra.period[2],
          y: processedData.acc_spectra.Z,
          line: {
            color: 'gray',
            width: 2,
            dash: 'dashdot',
          },
        })
        _plotData.push({
          type: 'scatter',
          mode: 'lines',
          name: 'SRSS',
          x0: processedData.acc_spectra.period[0],
          dx: processedData.acc_spectra.period[2],
          y: processedData.acc_spectra.E.map((e, i) => Math.sqrt((e ** 2) + (processedData.acc_spectra.N[i] ** 2))),
          line: {
            color: 'gray',
            width: 2,
            dash: 'dash',
          },
        })
        _plotData.push({
          type: 'scatter',
          mode: 'lines',
          name: 'Geomean',
          x0: processedData.acc_spectra.period[0],
          dx: processedData.acc_spectra.period[2],
          y: processedData.acc_spectra.E.map((e, i) => Math.sqrt(e * processedData.acc_spectra.N[i])),
          line: {
            color: 'gray',
            width: 2,
          },
        })
      }
      setPlotData(_plotData);
      setPlotLayout({
        title: {
          text: `Acceleration Response Spectra of ${record.streamId}<br />(Newmark Linear Method, alpha: 1/2, beta: 1/6)`,
        },
        xaxis: {
          type: 'log',
          dtick: 'log',
          autorange: true,
          title: 'Period (s)',
        },
        yaxis: {
          title: 'Pseudo Spectral Acceleration (cm/s<sup>2</sup>)',
        }
      });
    } else if (displayDataType === 'ACC') {
      if (record.id in dataCache && 'preprocessed' in dataCache[record.id]) {
        const { preprocessed } = dataCache[record.id]
        PLOT_COMPONENTS_AND_YAXISES.forEach(([component, yaxis]) => {
          _plotData.push({
            type: 'scatter',
            mode: 'lines',
            name: 'unprocessed',
            legendgroup: 'unprocessed',
            x0: parseInt(preprocessed[component].startTime * 1000),
            dx: 10,
            y: preprocessed[component].values,
            xaxis: 'x',
            yaxis,
            line: {
              color: 'red',
              width: 1,
            },
            showlegend: yaxis === 'y',
          })
        })
      }
      if ('acc' in processedData) {
        PLOT_COMPONENTS_AND_YAXISES.forEach(([component, yaxis]) => {
          _plotData.push({
            type: 'scatter',
            mode: 'lines',
            name: 'processed',
            legendgroup: 'processed',
            x0: parseInt(processedData.acc[component].startTime * 1000),
            dx: 10,
            y: processedData.acc[component].values,
            xaxis: 'x',
            yaxis,
            line: {
              color: 'black',
              width: 1,
            },
            showlegend: yaxis === 'y',
          })
        })
      }
      setPlotData(_plotData);
      setPlotLayout({
        title: {
          text: `${record.eventOriginTimeStrKST} (KST), M<sub>L</sub> ${record.evnetMagnitudeML.toFixed(1)}, ${record.stationCode}, R = ${record.epicentralDistance.toFixed(0)} km`,
        },
        grid: {
          rows: 3,
          columns: 1,
          subplots:['xy','xy2', 'xy3'],
        },
        xaxis: {
          type: 'date',
        },
        yaxis: {
          title: 'E (cm/s<sup>2</sup>)',
          zeroline: false,
        },
        yaxis2: {
          title: 'N (cm/s<sup>2</sup>)',
          zeroline: false,
        },
        yaxis3: {
          title: 'Z (cm/s<sup>2</sup>)',
          zeroline: false,
        },
        // legend: {"orientation": "h"},
      });
    } else if (displayDataType === 'VEL') {
      if ('vel' in processedData) {
        PLOT_COMPONENTS_AND_YAXISES.forEach(([component, yaxis]) => {
          _plotData.push({
            type: 'scatter',
            mode: 'lines',
            name: 'processed',
            legendgroup: 'processed',
            x0: parseInt(processedData.vel[component].startTime * 1000),
            dx: 10,
            y: processedData.vel[component].values,
            xaxis: 'x',
            yaxis,
            line: {
              color: 'black',
              width: 1,
            },
            showlegend: yaxis === 'y',
          })
        })
      }
      setPlotData(_plotData);
      setPlotLayout({
        title: {
          text: `${record.eventOriginTimeStrKST} (KST), M<sub>L</sub> ${record.evnetMagnitudeML.toFixed(1)}, ${record.stationCode}, R = ${record.epicentralDistance.toFixed(0)} km`,
        },
        grid: {
          rows: 3,
          columns: 1,
          subplots:['xy','xy2', 'xy3'],
          // roworder:'bottom to top',
          // pattern: 'independent',
        },
        xaxis: {
          type: 'date',
        },
        yaxis: {
          title: 'E (cm/s)',
          zeroline: false,
        },
        yaxis2: {
          title: 'N (cm/s)',
          zeroline: false,
        },
        yaxis3: {
          title: 'Z (cm/s)',
          zeroline: false,
        },
        // legend: {"orientation": "h"},
      });
    } else if (displayDataType === 'DISP') {
      if ('disp' in processedData) {
        PLOT_COMPONENTS_AND_YAXISES.forEach(([component, yaxis]) => {
          _plotData.push({
            type: 'scatter',
            mode: 'lines',
            name: 'processed',
            legendgroup: 'processed',
            x0: parseInt(processedData.disp[component].startTime * 1000),
            dx: 10,
            y: processedData.disp[component].values,
            xaxis: 'x',
            yaxis,
            line: {
              color: 'black',
              width: 1,
            },
            showlegend: yaxis === 'y',
          })
        })
      }
      setPlotData(_plotData);
      setPlotLayout({
        title: {
          text: `${record.eventOriginTimeStrKST} (KST), M<sub>L</sub> ${record.evnetMagnitudeML.toFixed(1)}, ${record.stationCode}, R = ${record.epicentralDistance.toFixed(0)} km`,
        },
        grid: {
          rows: 3,
          columns: 1,
          subplots:['xy','xy2', 'xy3'],
          // roworder:'bottom to top',
          // pattern: 'independent',
        },
        xaxis: {
          type: 'date',
        },
        yaxis: {
          title: 'E (cm)',
          zeroline: false,
        },
        yaxis2: {
          title: 'N (cm)',
          zeroline: false,
        },
        yaxis3: {
          title: 'Z (cm)',
          zeroline: false,
        },
        // legend: {"orientation": "h"},
      });
    } 
  }, [displayDataType, record, dataCache, processedData])

  const downloadData = (dataType) => {
    const dtype = dataType.toLowerCase()
    if (!(dtype in processedData)) {
      alert(t('page.kesmdb.notingToDownload') + '\r\n' + t('page.kesmdb.clickRunButtonFirst'))
    } else {
      if (!agreeToTermsOfUse) {
        alert(t('form.message.agreeToTermsOfUse'))
        return
      }
      _saveAsTextFile(dtype, record, processedData, processParameters, processedTime)
    }
  }

  return (
    <div style={{position: 'fixed', zIndex: 10681, top: 0, left: 0, right: 0, bottom: 0}} 
      // onClick={(e) => {if (e.target === e.currentTarget) { onClose() }}}
    >
      <div id="modal1" className="modal open">
        <div className="modal-content">
          <div className="modal-title">Process {record && `(${record.id})`}</div>
          <div className="row" style={{height: '100%'}}>
            <div className="col l6 m12 s12">
              <form onSubmit={handleSubmit(onSubmit)}>
                <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5}}>1. Time window</h2>
                <div className="multiinput_container" style={{marginLeft: 10}}>
                  <div className="row">
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.timewindow.beginning">
                        <span>At the beginning (s)</span>
                      </label>
                      <input
                        type="number"
                        id="eventrecord.timewindow.beginning"
                        aria-label="Time window(beginning)"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.timewindow.beginning}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.timewindow.beginning}
                        className="multiinput inputmid"
                        {...register("timewindow.beginning.value")}
                      />
                    </div>
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.timewindow.end">
                        <span>At the end (s)</span>
                      </label>
                      <input
                        type="number"
                        id="eventrecord.timewindow.end"
                        aria-label="Time window(end)"
                        placeholder={parseInt(record.endTime - record.startTime)}
                        defaultValue={parseInt(record.endTime - record.startTime)}
                        className="multiinput inputmid"
                        {...register("timewindow.end.value")}
                      />
                    </div>
                  </div>
                </div>
                <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5}}>2. Cosine taper</h2>
                <div className="multiinput_container" style={{marginLeft: 10}}>
                  <div className="row">
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.cosinetaper.beginning.enabled">
                        <input 
                          type="checkbox" id="eventrecord.cosinetaper.beginning.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("cosinetaper.beginning.enabled")}
                        />
                        <span>At the beginning (%)</span>
                      </label>
                      <input
                        type="number"
                        aria-label="Cosine taper 값(beginning)"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.cosinetaper.beginning}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.cosinetaper.beginning}
                        className="multiinput inputmid"
                        {...register("cosinetaper.beginning.value")}
                      />
                    </div>
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.cosinetaper.end.enabled">
                        <input 
                          type="checkbox" id="eventrecord.cosinetaper.end.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("cosinetaper.end.enabled")}
                        />
                        <span>At the end (%)</span>
                      </label>
                      <input
                        type="number"
                        aria-label="Cosine taper 값(end)"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.cosinetaper.end}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.cosinetaper.end}
                        className="multiinput inputmid"
                        {...register("cosinetaper.end.value")}
                      />
                    </div>
                  </div>
                </div>
                <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5}}>3. Bandpass filter</h2>
                <div style={{paddingLeft: 20, paddingRight: 50}}>
                  ※ Zero padding (up to the number of data matched to the n-th power or 2) is added and removed before and after applying the bandpass filter.
                </div>
                <div className="multiinput_container" style={{marginLeft: 10}}>
                  <div className="row">
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.bandpassfilter.lowcut.enabled">
                        <input 
                          type="checkbox" id="eventrecord.bandpassfilter.lowcut.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("bandpassfilter.lowcut.enabled")}
                          // onChange={handleCheckedStateChanged}
                        />
                        <span>Low-cut (Hz)</span>
                      </label>
                      {/* <label className="datatable_select_radio" htmlFor="eventrecord.bandpassfilter.lowcut">
                        Low-cut (Hz)
                      </label> */}
                      <input
                        type="text" name="bandpassfilter.lowcut.value"
                        aria-label="Bandpass filter의 low-cut 주파수"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.bandpassfilter.lowcut}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.bandpassfilter.lowcut}
                        className="multiinput inputmid"
                        {...register("bandpassfilter.lowcut.value")}
                      />
                    </div>
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.bandpassfilter.highcut.enabled">
                        <input 
                          type="checkbox" id="eventrecord.bandpassfilter.highcut.enabled" className="filled-in with-gap"
                          name="bandpassfilter.highcut.enabled"
                          defaultChecked="true"
                          {...register("bandpassfilter.highcut.enabled")}
                        />
                        <span>High-cut (Hz)</span>
                      </label>
                      {/* <label className="datatable_select_radio" htmlFor="eventrecord.bandpassfilter.highcut">
                        High-cut (Hz)
                      </label> */}
                      <input
                        type="number"
                        aria-label="Bandpass filter의 high-cut 주파수"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.bandpassfilter.highcut}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.bandpassfilter.highcut}
                        className="multiinput inputmid"
                        {...register("bandpassfilter.highcut.value")}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.bandpassfilter.order">
                        <span style={{marginLeft: 25}}>
                          Filter order
                        </span>
                      </label>
                    </div>
                    <div className="col l2 m2 s4">
                      <select id="eventrecord.bandpassfilter.order"
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.bandpassfilter.order}
                        {...register("bandpassfilter.order.value")}
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                      {/* <input
                        type="text" name="bandpassfilter.order"
                        aria-label="Bandpass filter의 차수"
                        placeholder="5"
                        defaultValue="5"
                        className="multiinput inputmid"
                        // onBlur={handleInputValueChanged}
                      /> */}
                    </div>
                  </div>                  
                </div>
                <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5}}>4. Post-cosine taper</h2>
                <div className="multiinput_container" style={{marginLeft: 10}}>
                  <div className="row">
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.postcosinetaper.beginning.enabled">
                        <input 
                          type="checkbox" id="eventrecord.postcosinetaper.beginning.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("postcosinetaper.beginning.enabled")}
                        />
                        <span>At the beginning (%)</span>
                      </label>
                      <input
                        type="number"
                        aria-label="Cosine taper 값(beginning)"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.postcosinetaper.beginning}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.postcosinetaper.beginning}
                        className="multiinput inputmid"
                        {...register("postcosinetaper.beginning.value")}
                      />
                    </div>
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.postcosinetaper.end.enabled">
                        <input 
                          type="checkbox" id="eventrecord.postcosinetaper.end.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("postcosinetaper.end.enabled")}
                        />
                        <span>At the end (%)</span>
                      </label>
                      <input
                        type="number"
                        aria-label="Cosine taper 값(end)"
                        placeholder={PROCESS_DEFAULT_PARAMETERS.postcosinetaper.end}
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.postcosinetaper.end}
                        className="multiinput inputmid"
                        {...register("postcosinetaper.end.value")}
                      />
                    </div>
                  </div>
                </div>
                <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5}}>5. Baseline correction</h2>
                <div className="multiinput_container" style={{marginLeft: 10}}>
                  <div className="row">
                    <div className="col">
                        <label className="datatable_select_radio" htmlFor="eventrecord.baselinecorrection.enabled">
                          <input 
                            type="checkbox" id="eventrecord.baselinecorrection.enabled" className="filled-in with-gap"
                            defaultChecked="true"
                            {...register("baselinecorrection.enabled")}
                          />
                          <span>Fitting domain</span>
                        </label>
                      </div>
                      <div className="col l2 m2 s4">
                        <select id="eventrecord.baselinecorrection.domain"
                          defaultValue={PROCESS_DEFAULT_PARAMETERS.baselinecorrection.domain}
                          {...register("baselinecorrection.domain")}
                        >
                          <option value="VEL">VEL</option>
                          <option value="ACC">ACC</option>
                          <option value="DISP">DISP</option>
                        </select>
                      </div>
                    <div className="col">
                      <label className="datatable_select_radio" htmlFor="eventrecord.baselinecorrection.enabled">
                        {/* <input 
                          type="checkbox" id="eventrecord.baselinecorrection.enabled" className="filled-in with-gap"
                          defaultChecked="true"
                          {...register("baselinecorrection.enabled")}
                        /> */}
                        <span>Polynomial order</span>
                      </label>
                    </div>
                    <div className="col l2 m2 s4">
                      <select id="eventrecord.baselinecorrection.order"
                        defaultValue={PROCESS_DEFAULT_PARAMETERS.baselinecorrection.order}
                        {...register("baselinecorrection.order")}
                      >
                        <option value={2}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* <errors
                  errors={errors}
                  name="multipleErrorInput"
                  render={({ messages }) =>
                    messages &&
                    Object.entries(messages).map(([type, message]) => (
                      <p key={type}>{message}</p>
                    ))
                  }
                /> */}
                <div className="center-align row" style={{marginTop: 45}}>
                  <div className="dataselect_box">
                    {/* <span className="txt_title">활용 목적&nbsp;&nbsp;&nbsp;&nbsp;</span> */}
                    <span>※ {t('w.purposeOfUse')}&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    {/* <form className="inline-block"> */}
                      {PRUPOSES_OF_USE.map(purpose => (
                        <label key={purpose}>
                          <input className="with-gap" name="dataformat" type="radio"
                            checked={downloadPurpose === purpose}
                            onChange={(e) => {if (e.target.checked) {setDownloadPurpose(purpose)}}}
                          />
                          <span>{t('purposeOfUse.' + purpose)}</span>
                        </label>
                      ))}
                    {/* </form> */}
                  </div>                  
                  {/* <button type="button" className="btn-block waves-effect waves-light button-lg" onClick={handleApplyParameters}>Apply</button> */}
                  <button type="submit" className="btn-block waves-effect waves-light button-lg">
                    {t('w.run')}
                  </button>
                </div>
              </form>
            </div>
            <div className="col l6 m12 s12">
              <h2 style={{fontSize: "1.2rem", marginTop: 15, marginBottom: 5, textAlign: 'center'}}>Select data type</h2>
              <div className="row center-align">
                {DATA_TYPES.map(dataType => 
                  <label key={dataType} className="datatable_select_radio" htmlFor={`event-record-process.${dataType}`}>
                    <input type="radio" id={`event-record-process.${dataType}`} 
                      name='event-record-process.datatype' value={dataType} className="with-gap"
                      checked={displayDataType === dataType}
                      // onClick={e => setDisplayDataType(dataType)}
                      onChange={e => {if (e.target.checked) setDisplayDataType(dataType);}}
                    />
                    <span>{dataType} &nbsp;&nbsp;&nbsp;&nbsp;</span>
                  </label>  
                )}
              </div>
              <div className="row" style={{minHeight: 660}}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Plot
                    data={plotData}
                    layout={plotLayout}
                    // layout={layout}
                    config={{responsive: true}}
                    useResizeHandler={true}
                    style={{width: "90%", minHeight: 660, marginLeft: "5%"}}
                  />
                </Suspense>
              </div>
              <div className="center-align row">
                <label className="datatable_select_radio">
                  <input  name="stationinformation" className="filled-in" type="checkbox"
                    checked={agreeToTermsOfUse}
                    onChange={(e) => setAgreeToTermsOfUse(e.target.checked)}
                  />
                  <span>
                    <Trans i18nKey="page.kesmdb.agreement"
                      components={{link1: <Link to="/intro/terms" target="_blank" rel="noreferrer"/>}}
                    />
                  </span>
                </label>
              </div>
              <div className="center-align row">
                <button type="button" className="btn-block waves-effect waves-light button-lg"
                  onClick={e => downloadData(displayDataType)}
                >
                  {t('w.download')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <a className="modal-close waves-effect waves-green btn-flat" onClick={onClose}><i className="ion ion-md-close"></i></a>
      </div>
    </div>
  )
}